import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import clsx from 'clsx';
import {Person} from '@app/titles/models/person';
import {PersonLink} from '@app/people/person-link';
import {ImageSize, useImageSrc, useImageSrcSet} from '@app/images/use-image-src';
import {PersonIcon} from '@ui/icons/material/Person';
import {getPersonDisplayName} from '@app/people/person-display-name';

interface Props {
  person: Person;
  className?: string;
  size?: string;
  lazy?: boolean;
  srcSize?: ImageSize;
  rounded?: boolean;
  roundedStyle?: 'full' | 'soft';
}
export function PersonPoster({
  person,
  className,
  size,
  srcSize = 'lg',
  lazy = true,
  rounded = false,
  roundedStyle = 'full',
}: Props) {
  const {trans} = useTrans();
  const src = useImageSrc(person?.poster, {size: srcSize});
  const srcset = useImageSrcSet(person?.poster, {kind: 'profile'});

  const imageClassName = clsx(
    className,
    size,
    'bg-fg-base/4 object-cover',
    rounded
      ? roundedStyle === 'soft'
        ? 'aspect-square rounded-[30%]'
        : 'aspect-square rounded-full'
      : 'aspect-poster rounded',
    !src ? 'flex items-center justify-center' : 'block',
  );

  const image = src ? (
    <img
      decoding="async"
      className={imageClassName}
      draggable={false}
      loading={lazy ? 'lazy' : 'eager'}
      width={rounded ? 200 : 500}
      height={rounded ? 200 : 750}
      src={src}
      srcSet={srcset}
      sizes="(max-width: 640px) 35vw, (max-width: 1024px) 20vw, 180px"
      alt={trans(
        message('Cover image for :name', {
          values: {name: getPersonDisplayName(person)},
        }),
      )}
    />
  ) : (
    <span className={imageClassName}>
      <PersonIcon className="max-w-[60%] text-divider" size="text-5xl" />
    </span>
  );

  return (
    <PersonLink person={person} className="flex-shrink-0">
      {image}
    </PersonLink>
  );
}
